import { useEffect, useState, useRef, useMemo } from "react";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import { api } from "@/lib/api";

/**
 * NotificationBell — categorized dropdown with tab filters, mark-all-read
 * action, and day-grouped list.
 *
 * Polls unread count every 30s; loads the last 50 notifications when opened.
 */
const TABS = [
    { key: "all", label: "All" },
    { key: "trades", label: "Trades" },
];

// Notification-type → tab classifier. Everything that isn't a trade lands
// in "all" — we no longer separate Learn / Compliance / System tabs (Learn
// was retired platform-wide; Compliance was folded into KYC-less flow;
// System messages surface in the general feed).
const TYPE_TAB = {
    trade_fill: "trades",
    trade_close: "trades",
    breach: "trades",
    drawdown_breach: "trades",
    copy_replicated: "trades",
    copy_request: "trades",
};

function classifyTab(type) {
    // Anything not in the trades set falls back to "all" (visible in the
    // default tab). We no longer emit a separate "system" bucket.
    return TYPE_TAB[type] || "all";
}

const EMPTY_COPY = {
    all: { title: "You're all caught up.", body: "New activity will land here in real-time." },
    trades: { title: "No trade alerts yet.", body: "Fills, closes and drawdown pings appear here." },
};

export default function NotificationBell() {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [activeTab, setActiveTab] = useState("all");
    const dropRef = useRef(null);

    async function refreshCount() {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setCount(data.unread || 0);
        } catch { /* ignore */ }
    }

    async function loadList() {
        setBusy(true);
        try {
            const { data } = await api.get("/notifications");
            setItems(data || []);
        } catch { /* ignore */ }
        setBusy(false);
    }

    async function toggle() {
        const next = !open;
        setOpen(next);
        if (next) {
            await loadList();
        }
    }

    async function markAllRead() {
        try {
            await api.post("/notifications/mark-read");
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setCount(0);
        } catch { /* ignore */ }
    }

    useEffect(() => {
        refreshCount();
        const id = setInterval(refreshCount, 30000);
        return () => clearInterval(id);
    }, []);

    // Close on outside click
    useEffect(() => {
        function onDoc(e) {
            if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // Per-tab unread badges
    const tabCounts = useMemo(() => {
        const c = { all: 0, trades: 0, learn: 0, compliance: 0, system: 0 };
        for (const n of items) {
            if (n.read) continue;
            const t = classifyTab(n.type);
            c[t] = (c[t] || 0) + 1;
            c.all += 1;
        }
        return c;
    }, [items]);

    // Filter + group by day
    const groups = useMemo(() => {
        const filtered = activeTab === "all"
            ? items
            : items.filter((n) => classifyTab(n.type) === activeTab);
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 86400_000).toDateString();
        const buckets = { Today: [], Yesterday: [], Earlier: [] };
        for (const n of filtered) {
            const d = new Date(n.ts).toDateString();
            if (d === today) buckets.Today.push(n);
            else if (d === yesterday) buckets.Yesterday.push(n);
            else buckets.Earlier.push(n);
        }
        return Object.entries(buckets).filter(([, arr]) => arr.length);
    }, [items, activeTab]);

    const activeEmpty = EMPTY_COPY[activeTab] || EMPTY_COPY.all;

    return (
        <div className="relative" ref={dropRef}>
            <button
                onClick={toggle}
                data-testid="notification-bell"
                aria-label="Notifications"
                className="p-2 rounded-md hover:bg-secondary transition-colors relative"
            >
                <Bell className="w-4 h-4" />
                {count > 0 && (
                    <span
                        data-testid="notification-badge"
                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center"
                    >
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-[360px] max-h-[520px] overflow-hidden rounded-md border border-border bg-card shadow-2xl z-50 flex flex-col"
                    data-testid="notification-dropdown"
                >
                    {/* Header */}
                    <div className="p-3 border-b border-border flex items-center justify-between">
                        <span className="overline">Notifications</span>
                        <button
                            onClick={markAllRead}
                            disabled={tabCounts.all === 0}
                            data-testid="notification-mark-all-read"
                            className="text-[11px] font-semibold text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed inline-flex items-center gap-1"
                        >
                            <CheckCheck className="w-3.5 h-3.5" />
                            Mark all read
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-0.5 px-2 pt-2 border-b border-border overflow-x-auto">
                        {TABS.map((t) => {
                            const active = t.key === activeTab;
                            const unread = tabCounts[t.key] || 0;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    data-testid={`notif-tab-${t.key}`}
                                    className={`px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap rounded-t-md border-b-2 transition-colors ${
                                        active
                                            ? "border-primary text-foreground"
                                            : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {t.label}
                                    {unread > 0 && (
                                        <span
                                            data-testid={`notif-tab-count-${t.key}`}
                                            className={`ml-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold ${
                                                active
                                                    ? "bg-primary text-primary-foreground"
                                                    : "bg-secondary text-foreground"
                                            }`}
                                        >
                                            {unread > 99 ? "99+" : unread}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto">
                        {busy && (
                            <div className="p-4 text-xs text-muted-foreground text-center">Loading…</div>
                        )}
                        {!busy && groups.length === 0 && (
                            <div
                                data-testid={`notification-empty-${activeTab}`}
                                className="flex flex-col items-center justify-center text-center py-10 px-6"
                            >
                                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                                    <Inbox className="w-5 h-5" />
                                </div>
                                <div className="text-sm font-semibold">{activeEmpty.title}</div>
                                <div className="text-[11px] text-muted-foreground max-w-[240px] mt-1">
                                    {activeEmpty.body}
                                </div>
                            </div>
                        )}
                        {!busy && groups.map(([label, arr]) => (
                            <div key={label}>
                                <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-card sticky top-0">
                                    {label}
                                </div>
                                {arr.map((n) => (
                                    <NotifRow key={n.id} n={n} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function NotifRow({ n }) {
    const tab = classifyTab(n.type);
    const dotClass =
        tab === "trades" ? "bg-primary"
            : tab === "learn" ? "bg-emerald-500"
            : tab === "compliance" ? "bg-amber-500"
            : "bg-muted-foreground";
    return (
        <div
            className={`px-3 py-2 border-b border-border/60 last:border-0 hover:bg-secondary/60 ${
                n.read ? "opacity-70" : ""
            }`}
            data-testid={`notif-${n.id}`}
        >
            <div className="flex items-start gap-2">
                {!n.read && <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dotClass}`} />}
                <div className="min-w-0 flex-1">
                    <div className="text-xs text-foreground leading-snug">{n.message}</div>
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span className="uppercase font-bold tracking-widest">{n.type}</span>
                        <span className="mono">{new Date(n.ts).toLocaleString("en-KE")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
